import React, { useEffect, useRef } from "react";
import { CheckSquare, FileText, Star, Zap } from "lucide-react";

/**
 * SlashCommandMenu — popover that appears above the capture bar when user types `/`.
 *
 * Props:
 *   open              boolean
 *   query             string (text after the "/", used to filter commands)
 *   onSelect(cmd)     fires when user picks; cmd = "task" | "brief" | "priority" | "jot"
 *   onClose           fires on Esc or blur
 *   anchorRef         capture bar input ref (popover positions itself above it)
 *
 * Keyboard nav is controlled externally (by CaptureRow) — this component just
 * renders and exposes arrow/enter handlers via static helpers. Consumers call:
 *   SlashCommandMenu.filter(query)     → filtered commands
 *   SlashCommandMenu.select(cmd)       → navigation helper
 */

const ALL_COMMANDS = [
  { id: "task", label: "New task", hint: "opens full form", icon: CheckSquare, color: "#a78bfa" },
  { id: "brief", label: "New brief", hint: "opens full form", icon: FileText, color: "#60a5fa" },
  { id: "priority", label: "Add to today's priorities", hint: "max 3", icon: Star, color: "#fbbf24" },
  { id: "jot", label: "Quick jot", hint: "default — just press Enter", icon: Zap, color: "#5eead4" },
];

export default function SlashCommandMenu({ open, query, selectedIdx, onSelect, onHover }) {
  const listRef = useRef(null);
  const filtered = React.useMemo(() => filterCommands(query), [query]);

  useEffect(() => {
    if (!open) return;
    // Scroll selected into view if needed
    if (listRef.current) {
      const sel = listRef.current.querySelector("[data-selected='true']");
      if (sel) sel.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIdx, open, filtered]);

  if (!open || filtered.length === 0) return null;

  return (
    <div
      ref={listRef}
      style={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        marginTop: 8,
        background: "var(--s1)",
        border: "1px solid var(--bdrh)",
        borderRadius: 14,
        padding: 6,
        boxShadow: "0 16px 48px rgba(0,0,0,.5), 0 2px 6px rgba(0,0,0,.25)",
        zIndex: 30,
        animation: "fadeIn .12s ease-out",
      }}
      role="listbox"
      aria-label="Slash commands"
    >
      {filtered.map((cmd, i) => {
        const Icon = cmd.icon;
        const selected = i === selectedIdx;
        return (
          <button
            key={cmd.id}
            role="option"
            aria-selected={selected}
            data-selected={selected ? "true" : "false"}
            onMouseEnter={() => onHover?.(i)}
            onClick={(e) => { e.preventDefault(); onSelect?.(cmd.id); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 12px",
              borderRadius: 10,
              cursor: "pointer",
              background: selected ? "var(--s2)" : "transparent",
              border: "none",
              width: "100%",
              textAlign: "left",
              color: selected ? "var(--t1)" : "var(--t2)",
              transition: "background .1s",
              fontFamily: "var(--font-sans)",
            }}
            type="button"
          >
            <span
              style={{
                width: 26,
                height: 26,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 6,
                background: `${cmd.color}22`,
                color: cmd.color,
                flexShrink: 0,
              }}
            >
              <Icon size={14} />
            </span>
            <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{cmd.label}</span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--t3)",
                padding: "2px 7px",
                border: "1px solid var(--bdr)",
                borderRadius: 5,
                background: "var(--s0, var(--bg))",
              }}
            >
              /{cmd.id}
            </span>
            <span
              style={{
                fontSize: 12,
                color: "var(--t3)",
                marginLeft: 4,
              }}
            >
              {cmd.hint}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function filterCommands(query) {
  const q = (query || "").toLowerCase().trim();
  if (!q) return ALL_COMMANDS;
  return ALL_COMMANDS.filter((c) => c.id.includes(q) || c.label.toLowerCase().includes(q));
}

SlashCommandMenu.filterCommands = filterCommands;
SlashCommandMenu.ALL_COMMANDS = ALL_COMMANDS;
